import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAddress, getAddress } from 'viem';
import { Wallet, ExternalLink, AlertCircle, CheckCircle, Copy, Unlink, Plus, Pencil, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth';
import { useWallet, NETWORK_CONFIG } from '@/hooks/useWallet';
import { apiClient } from '@/services/api';
import { errorLog } from '@/utils/logger';
import { captureEvent } from '@/utils/posthog';
import { HEP_TOKEN_ADDRESS } from '@/contracts/HepToken';

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const { address, isConnected, isConnecting, chainId, connect, disconnect, switchToLocalChain, addNetworkToWallet, addHepTokenToWallet, isMobile, metaMaskDeepLink } = useWallet();
  const [isSaving, setIsSaving] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const [manualConfirming, setManualConfirming] = useState(false);

  const manualAddressValid = isAddress(manualAddress.trim(), { strict: false });
  const manualAddressChecksummed = manualAddressValid ? getAddress(manualAddress.trim()) : null;

  // Known HEP contract addresses that must never be used as a wallet.
  const HEP_PROXY_ADDRESS = '0x5Cb3eC8806E50ED7B92b663E41d2A981577380D5';
  const isBlockedAddress = (addr: string) =>
    [HEP_TOKEN_ADDRESS, HEP_PROXY_ADDRESS].some(
      (blocked) => blocked.toLowerCase() === addr.toLowerCase()
    );
  const manualAddressIsContract = !!manualAddressChecksummed && isBlockedAddress(manualAddressChecksummed);

  const { data: balanceData, isError: balanceError } = useQuery({
    queryKey: ['hep-balance', user?.walletAddress],
    queryFn: () => apiClient.users.getBalance(),
    enabled: !!user?.walletAddress,
    staleTime: 15000,
    refetchInterval: 30000,
    retry: 1,
  });

  const isWrongChain = isConnected && chainId !== NETWORK_CONFIG.chainId;
  const isWalletLinked = user?.walletAddress && address && user.walletAddress.toLowerCase() === address.toLowerCase();

  const copyAddress = async (addr: string) => {
    try {
      await navigator.clipboard.writeText(addr);
      toast.success('Address copied to clipboard!');
    } catch {
      try {
        const el = document.createElement('textarea');
        el.value = addr;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        toast.success('Address copied to clipboard!');
      } catch {
        toast.error('Failed to copy address');
      }
    }
  };

  const handleLinkWallet = async () => {
    if (!address) return;

    if (isBlockedAddress(address)) {
      toast.error('This is the HEP token contract address, not a wallet. Enter your personal wallet address.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await apiClient.users.updateProfile({
        walletAddress: address,
      });

      if (response.success && response.user) {
        updateUser(response.user);
        captureEvent('wallet_connected', { method: 'metamask' });
        toast.success('Wallet linked successfully!');
      }
    } catch (error: any) {
      errorLog('Failed to link wallet:', error);
      toast.error(error.message || 'Failed to link wallet');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualLink = async () => {
    if (!manualAddressChecksummed) return;

    if (manualAddressIsContract) {
      toast.error('This is the HEP token contract address, not a wallet. Enter your personal wallet address.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await apiClient.users.updateProfile({
        walletAddress: manualAddressChecksummed,
      });

      if (response.success && response.user) {
        updateUser(response.user);
        captureEvent('wallet_connected', { method: 'manual' });
        toast.success('Wallet linked successfully!');
        setShowManualEntry(false);
        setManualAddress('');
        setManualConfirming(false);
      }
    } catch (error: any) {
      errorLog('Failed to link wallet:', error);
      toast.error(error.message || 'Failed to link wallet');
    } finally {
      setIsSaving(false);
    }
  };

  const closeManualEntry = () => {
    setShowManualEntry(false);
    setManualAddress('');
    setManualConfirming(false);
  };

  const handleUnlinkWallet = async () => {
    try {
      setIsSaving(true);
      const response = await apiClient.users.updateProfile({
        walletAddress: null,
      });

      if (response.success && response.user) {
        updateUser(response.user);
        toast.success('Wallet unlinked successfully!');
      }
    } catch (error: any) {
      errorLog('Failed to unlink wallet:', error);
      toast.error(error.message || 'Failed to unlink wallet');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="font-bold text-4xl text-white">Profile</h1>
        <p className="mt-1 text-lg text-white">Manage your account settings and wallet connection</p>
      </div>

      {/* User Information Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">User Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <p className="text-gray-900">{user?.fullName}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-gray-900">{user?.email}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <p className="text-gray-900">{user?.username || 'Not set'}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                user?.role === 'admin' 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {user?.role}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Wallet Connection Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Wallet size={20} className="text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Wallet Connection</h3>
        </div>

        <div className="space-y-4">
          {/* Current Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Linked Wallet
                  <span className="text-xs font-normal text-gray-500 ml-2">(Saved in your profile for receiving payments)</span>
                </label>
                {user?.walletAddress ? (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-gray-900 break-all">
                      {user.walletAddress}
                    </span>
                    <button
                      onClick={() => copyAddress(user.walletAddress!)}
                      className="p-1 text-gray-500 hover:text-gray-700 touch-manipulation"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={handleUnlinkWallet}
                      disabled={isSaving}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-200 hover:border-red-400 rounded disabled:opacity-50 touch-manipulation"
                      title="Remove the linked wallet address from your profile"
                    >
                      <Unlink size={12} />
                      {isSaving ? 'Unlinking...' : 'Unlink'}
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-500">No wallet linked</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Connected Wallet
                  <span className="text-xs font-normal text-gray-500 ml-2">(Currently connected MetaMask account)</span>
                </label>
                {isConnected && address ? (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-gray-900 break-all">
                      {address}
                    </span>
                    <button
                      onClick={() => copyAddress(address)}
                      className="p-1 text-gray-500 hover:text-gray-700 touch-manipulation"
                    >
                      <Copy size={16} />
                    </button>
                    {isWalletLinked && <CheckCircle size={16} className="text-green-600" />}
                  </div>
                ) : (
                  <p className="text-gray-500">No wallet connected</p>
                )}
              </div>

              {user?.walletAddress && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HEP Balance
                    <span className="text-xs font-normal text-gray-500 ml-2">(live on-chain balance)</span>
                  </label>
                  <p className="text-gray-900 font-mono">
                    {balanceError
                      ? <span className="text-red-500 text-sm">Blockchain unavailable</span>
                      : `${balanceData?.data?.balanceFormatted ?? '—'} HEP`}
                  </p>
                </div>
              )}

              {isWrongChain && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <AlertCircle size={16} className="text-yellow-600 flex-shrink-0" />
                  <span className="text-sm text-yellow-800">
                    {`Wrong network. Please switch to ${NETWORK_CONFIG.name} (Chain ID: ${NETWORK_CONFIG.chainId})`}
                  </span>
                </div>
              )}

              {isWrongChain && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md text-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-blue-900">Add Network Manually in MetaMask</span>
                    {!isMobile && typeof window !== 'undefined' && !!window.ethereum && (
                      <button
                        onClick={addNetworkToWallet}
                        className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 font-medium"
                      >
                        Add to MetaMask
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: 'Network Name', value: NETWORK_CONFIG.name },
                      { label: 'RPC URL', value: NETWORK_CONFIG.rpcUrl },
                      { label: 'Chain ID', value: String(NETWORK_CONFIG.chainId) },
                      { label: 'Currency Symbol', value: NETWORK_CONFIG.symbol },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between gap-2 bg-white border border-blue-100 rounded px-3 py-2">
                        <div>
                          <div className="text-xs text-gray-500">{label}</div>
                          <div className="font-mono text-xs text-gray-800 break-all">{value}</div>
                        </div>
                        <button
                          onClick={() => copyAddress(value)}
                          className="flex-shrink-0 text-blue-600 hover:text-blue-800"
                          title="Copy"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-700">
                    In MetaMask: click the network selector → "Add a custom network" → paste these values.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {!isConnected ? (
              typeof window !== 'undefined' && !window.ethereum && isMobile ? (
                <div className="text-center">
                  <a
                    href={metaMaskDeepLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 font-medium touch-manipulation"
                  >
                    <Wallet size={20} />
                    Open in MetaMask
                  </a>
                  <p className="mt-2 text-xs text-gray-500">Opens this page in MetaMask's built-in browser</p>
                </div>
              ) : (
                <button
                  onClick={connect}
                  disabled={isConnecting}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium touch-manipulation"
                >
                  <Wallet size={20} />
                  {isConnecting ? 'Connecting...' : 'Connect MetaMask'}
                </button>
              )
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={disconnect}
                  className="flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 font-medium touch-manipulation"
                >
                  <Unlink size={20} />
                  Disconnect
                </button>

                {isWrongChain ? (
                  <button
                    onClick={switchToLocalChain}
                    className="flex items-center justify-center gap-2 bg-yellow-600 text-white px-4 py-3 rounded-lg hover:bg-yellow-700 font-medium touch-manipulation"
                  >
                    <ExternalLink size={20} />
                    Switch Network
                  </button>
                ) : !isWalletLinked && address ? (
                  <button
                    onClick={handleLinkWallet}
                    disabled={isSaving}
                    className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium touch-manipulation"
                  >
                    <CheckCircle size={20} />
                    {isSaving ? 'Linking...' : 'Link Wallet'}
                  </button>
                ) : isWalletLinked ? (
                  <button
                    onClick={handleUnlinkWallet}
                    disabled={isSaving}
                    className="flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium touch-manipulation"
                  >
                    <Unlink size={20} />
                    {isSaving ? 'Unlinking...' : 'Unlink Wallet'}
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {/* Manual Wallet Entry */}
          {!showManualEntry ? (
            <button
              onClick={() => setShowManualEntry(true)}
              className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg border border-dashed border-gray-300 hover:border-gray-400 touch-manipulation"
            >
              <Pencil size={16} />
              {user?.walletAddress ? 'Change wallet address manually' : 'Enter wallet address manually'}
            </button>
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {user?.walletAddress ? 'Change linked wallet address' : 'Link wallet address manually'}
                </label>
                <button
                  onClick={closeManualEntry}
                  className="p-1 text-gray-500 hover:text-gray-700 touch-manipulation"
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </div>

              {!manualConfirming ? (
                <>
                  <input
                    type="text"
                    value={manualAddress}
                    onChange={(e) => setManualAddress(e.target.value)}
                    placeholder="0x…"
                    autoComplete="off"
                    spellCheck={false}
                    className="w-full font-mono text-sm text-gray-900 bg-white placeholder-gray-400 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {manualAddress.trim() && !manualAddressValid && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <AlertCircle size={14} className="flex-shrink-0" />
                      Not a valid Ethereum address
                    </div>
                  )}
                  {manualAddressIsContract && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <AlertCircle size={14} className="flex-shrink-0" />
                      This is the HEP token contract address, not a wallet. Enter your personal wallet address.
                    </div>
                  )}
                  <button
                    onClick={() => setManualConfirming(true)}
                    disabled={!manualAddressValid || manualAddressIsContract}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium touch-manipulation"
                  >
                    Continue
                  </button>
                </>
              ) : (
                <>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle size={16} className="text-yellow-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-yellow-800">Please double-check this address</span>
                    </div>
                    <p className="font-mono text-sm text-gray-900 break-all bg-white border border-yellow-100 rounded px-2 py-1.5">
                      {manualAddressChecksummed}
                    </p>
                    <p className="text-xs text-yellow-800">
                      All HEP payouts will be sent to this address. Ownership is not verified —
                      payments sent to a wrong address cannot be recovered.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setManualConfirming(false)}
                      className="flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 font-medium touch-manipulation"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleManualLink}
                      disabled={isSaving}
                      className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium touch-manipulation"
                    >
                      <CheckCircle size={16} />
                      {isSaving ? 'Linking...' : 'Confirm & Link'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Add HEP Token to MetaMask */}
          {(isConnected && !isWrongChain) || isMobile ? (
            <div className="space-y-3">
              {isConnected && !isWrongChain ? (
                <button
                  onClick={addHepTokenToWallet}
                  className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 font-medium touch-manipulation"
                >
                  <Plus size={20} />
                  Add HEP Token to MetaMask
                </button>
              ) : (
                <div className="text-center">
                  <a
                    href={metaMaskDeepLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 font-medium touch-manipulation"
                  >
                    <Plus size={20} />
                    Open in MetaMask
                  </a>
                  <p className="mt-2 text-xs text-gray-500">Opens this page in MetaMask's built-in browser</p>
                </div>
              )}
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
                <p className="font-medium text-gray-700 mb-1">MetaMask Token Setup</p>
                <p>Click the button above to automatically add HEP. Or add manually:</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li className="break-all">Token Contract: <code className="bg-gray-200 px-1.5 py-0.5 rounded text-sm ml-1">{HEP_TOKEN_ADDRESS}</code></li>
                  <li>Symbol: <strong>HEP</strong></li>
                  <li>Decimals: <strong>2</strong></li>
                </ul>
              </div>
            </div>
          ) : null}

          {/* Help Text */}
          <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
            <p className="font-medium mb-1">Why link a wallet?</p>
            <p>
              Linking your MetaMask wallet allows you to receive HEP token rewards for your approved work sessions.
              {`Make sure you're connected to the ${NETWORK_CONFIG.name} (Chain ID: ${NETWORK_CONFIG.chainId}).`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;